const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form

    /**
     * @type {CollectionFiltersPaginationRender}
     * @public
     */
    this.paginationRenderer = new CollectionFiltersPaginationRender(this)

    const additionalExternalElements = [
      'filter-count',
      'filter-count-drawer',
      'drawer-footer',
      'tool-clear',
    ]
    this.additionalExternalElements = additionalExternalElements

    this.itemContainer = this.form.externalEls['item-container'].element
    this.productGrid = this.form.externalEls['product-grid'].element
  }

  async preload (section, url) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.filterData.push({ html, url })

    const currentPage = this.form.searchParams.current.get('page')
    this.form.searchParams.current.set(
      'page',
      (Number(currentPage) - 1).toString(),
    )
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @returns {void}
   */
  async fromHistory () {
    const searchParams = new URLSearchParams(window.location.search)
    const page = parseInt(searchParams.get('page'))

    const loadPrevious = this.form.externalEls['load-previous']?.element

    if (!history.state?.selectedProduct) {
      if (loadPrevious && page > 1) {
        loadPrevious.style.opacity = 1
        loadPrevious.style.visibility = 'visible'
      }
      return
    }

    if (loadPrevious && loadPrevious.querySelector('a')) {
      loadPrevious.querySelector('a').click()
    }
  }

  /**
   * @param {String} html
   * @param {SubmitEvent|PointerEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    this.renderFilters(parsedHTML, e)
    this.renderProductGridContainer(parsedHTML, e)
    this.paginationRenderer.render(parsedHTML)

    this.form.sortSortableItems()

    this.form.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = html
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')
    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))
    const countsToRender = facetDetailElements.find(el => matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetsToRender,
      countsToRender,
    })

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)

      if (!el) return

      el.innerHTML = element.innerHTML
    })

    this.renderAdditionalElements = this.renderAdditionalElements.bind(this)
    this.renderAdditionalElements(parsedHTML)

    if (countsToRender) {
      this.renderSelectedFacetCount(
        countsToRender,
        e.target.closest(detailSelector),
      )
    }
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {
    const additionalExternalElements = [
      'filter-count',
      'filter-count-drawer',
      'drawer-footer',
      'tool-clear',
    ]

    additionalExternalElements.forEach(additionalElement => {
      const sourceElement = getElement(
        `[${this.form._externalElIdentifier}="${additionalElement}"]`,
        { context: parsedHTML },
      )
      const targetElement = this.additionalExternalElements[additionalElement]

      if (!targetElement || !sourceElement) return

      if (targetElement.innerHTML === sourceElement.innerHTML) return

      targetElement.innerHTML = sourceElement.innerHTML
    })
  }

  /**
   * @param {String} html
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const direction = this.getPaginationDirection(e)
    const parsedHTML = html

    const newItems = document.createDocumentFragment()
    const newElements = getElements(
      `[${this.form._externalElIdentifier}="item"]`,
      {
        context: parsedHTML,
      },
    )

    const itemContainer = this.itemContainer
    const productGrid = this.productGrid

    const newProductGrid = parsedHTML.querySelector(
      `[${this.form._externalElIdentifier}="product-grid"]`,
    )
    const newCurrentPage = newProductGrid.getAttribute('data-current-page')
    const newLastPage = newProductGrid.getAttribute('data-last-page')

    productGrid.setAttribute('data-current-page', newCurrentPage)
    itemContainer.setAttribute('data-page', newCurrentPage)
    productGrid.setAttribute('data-last-page', newLastPage)

    newElements.forEach(el => newItems.appendChild(el))

    if (direction == null) {
      this.itemContainer.innerHTML = ''
      this.itemContainer.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.itemContainer.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.itemContainer.append(newItems)

      this.observer = new IntersectionObserver(
        this.handleNextObservation.bind(this),
        {
          rootMargin: '2400px',
        },
      )
      this.observer.observe(this.form.externalEls['load-more'].element)
    }

    const product = document.querySelector(`#${history.state?.selectedProduct}`)

    if (product) {
      window.scrollTo(0, product.offsetTop)
      history.pushState({}, '', window.location.href)
    }
  }

  handleNextObservation (entries) {
    const nextPagination = entries[0]
    const nextPaginationButton = nextPagination.target.querySelector('a')

    if (!nextPagination || !nextPaginationButton) return

    nextPaginationButton.closest(
      '[data-collection-filters-form-external-el="load-more"]',
    ).style.opacity = 0

    const isPaginationInView = nextPagination.isIntersecting

    if (isPaginationInView) {
      nextPaginationButton?.click()

      this.observer.disconnect()
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    const paginationElement = e.target?.closest(`.clc-ProductGrid_Pagination`)

    if (!paginationElement) return null

    return paginationElement.querySelector(
      '[data-collection-filters-form-external-el="load-previous"]',
    )
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets'].selector,
        this.form.externalEls['active-facets'].selector,
      ]
    }

    return [this.form.els['active-facets'].selector]
  }

  set additionalExternalElements (selectors) {
    const additionalExternalElements = {}
    selectors.forEach(selector => {
      const element = getElement(this.form.externalEls[selector]?.selector, {
        context: document,
      })
      if (element) additionalExternalElements[selector] = element
    })

    this._additionalExternalElements = additionalExternalElements
  }

  get additionalExternalElements () {
    return this._additionalExternalElements
  }

  set itemContainer (el) {
    this._itemContainer = el
  }

  get itemContainer () {
    return this._itemContainer
  }

  set productGrid (el) {
    this._productGrid = el
  }

  get productGrid () {
    return this._productGrid
  }
}

class CollectionFiltersPaginationRender {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = renderer

    this.loadMoreEl = this.form.externalEls['load-more'].element
    this.loadPreviousEl = this.form.externalEls['load-previous'].element
  }

  /**
   * @param {string} html
   */
  render (html) {
    const parsedHTML = html

    this.renderLoadPrevious(parsedHTML)
    this.renderLoadMore(parsedHTML)
    this.setLoadMoreUrl()
  }

  setLoadMoreUrl () {
    const loadMoreAnchorEl = getElement('a', { context: this.loadMoreEl })

    if (!loadMoreAnchorEl) return

    const url = new URL(loadMoreAnchorEl.href)
    const queryParams = new URLSearchParams(url.search)

    queryParams.set('page', this.currentHighestPage + 1)
    url.search = queryParams.toString()

    loadMoreAnchorEl.setAttribute('href', url.toString())
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious) {
      return (this.loadPreviousEl.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    this.loadPreviousEl.innerHTML = getElement(
      `[${this.form._externalElIdentifier}="load-previous"]`,
      { context: html },
    ).innerHTML
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldShowLoadMore) {
      return (this.loadMoreEl.innerHTML = '')
    }

    if (!this.shouldRerenderLoadMore) return

    this.loadMoreEl.innerHTML = getElement(
      `[${this.form._externalElIdentifier}="load-more"]`,
      { context: html },
    ).innerHTML
  }

  get form () {
    return this.renderer.form
  }

  set loadMoreEl (el) {
    this._loadMoreEl = el
  }

  get loadMoreEl () {
    return this._loadMoreEl
  }

  set loadPreviousEl (el) {
    this._loadPreviousEl = el
  }

  get loadPreviousEl () {
    return this._loadPreviousEl
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `[${this.form._externalElIdentifier}="item"][data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldShowLoadMore () {
    const anElementFromTheLastPageExists = getElement(
      `[${this.form._externalElIdentifier}="item"][data-page="${this.lastPage}"]`,
    )
    if (anElementFromTheLastPageExists) return false

    return Number(this.currentPage) <= this.lastPage
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.form.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    const gridItems = getElements(`[${this.form._externalElIdentifier}="item"]`)

    return Number(gridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    const gridItems = getElements(`[${this.form._externalElIdentifier}="item"]`)

    return Number(gridItems[gridItems.length - 1].dataset.page) || 1
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(
      this.form.externalEls['product-grid'].element.dataset.lastPage,
    )
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary']
  }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  static isPaginating = false

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  connectedCallback () {
    super.connectedCallback()

    this.renderer.fromHistory()
    this.sortSortableItems()
    this.toggleFiltersBasedOnScreenSize()
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 500)
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(
      this.els.facets.element,
      'keyup',
      this.onKeyUp.bind(this),
    )

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onLinkClick.bind(this))

    this.mediaQueryList.addEventListener(
      'change',
      this.toggleFiltersBasedOnScreenSize.bind(this),
    )
  }

  toggleFiltersBasedOnScreenSize () {
    this.toggleFilterDetails(this.mediaQueryList.matches)
  }

  toggleFilterDetails (boolean) {
    this.els.facet.elements.forEach(detail => {
      detail.open = boolean
    })
  }

  get mediaQueryList () {
    return window.matchMedia('(min-width: 768px)')
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.initial
    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    this.form.renderPage({ searchParams, updateURLHash: false, e })
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams, e })

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return
    if (this.constructor.isPaginating) return

    this.constructor.isPaginating = true

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    const isPrevious = e.target.closest(
      '[data-collection-filters-form-external-el="load-previous"]',
    )

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      e,
      updateURLHash: !isPrevious,
    })
  }

  /**
   * @param {PointerEvent} e
   */
  onLinkClick (e) {
    if (!e.target.closest('a')) return

    const linkHasPageReference = e.target.closest(`[data-page]`)
    if (!linkHasPageReference) return true

    e.preventDefault()

    const page = linkHasPageReference.dataset.page
    this.searchParams.current.set('page', page)

    const newUrl = `${window.location.pathname}${this.searchParams.current &&
      '?'.concat(this.searchParams.current.toString())}`

    const state = {
      selectedProduct: linkHasPageReference.id,
      scrollPosition: window.scrollY,
    }

    history.pushState(state, '', newUrl)

    window.location.href = e.target.getAttribute('href')
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent|PointerEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    const key = 'renderComplete'

    const renderComplete = new CustomEvent('renderComplete', {
      detail: {
        key,
      },
    })

    document.dispatchEvent(renderComplete)

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  sortSortableItems () {
    if (
      !this.els?.['sortable-parent']?.elements ||
      !this.els?.sortable?.elements
    ) {
      return
    }

    const sortableParentEls = getElements(this.els['sortable-parent'].selector)

    sortableParentEls.forEach(sortableParentEl => {
      const sortableEls = Array.from(
        getElements(this.els.sortable.selector, {
          context: sortableParentEl,
        }),
      )
      const sortedEls = sortableEls.sort((a, b) => {
        return (
          Number(a.dataset?.positionIndex) - Number(b.dataset?.positionIndex)
        )
      })
      sortableParentEl.innerHTML = ''
      sortedEls.forEach(sortedEl => sortableParentEl.append(sortedEl))

      try {
        window.iWish && setTimeout(() => window.iWish.checkIwishColl(), 1500)
      } catch (e) {}
    })
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      {
        searchParams: searchParams.toString(),
      },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )

    if (val === false) {
      this.constructor.isPaginating = false
    }
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    getElements('input', { context: this }).forEach(el =>
      this.listeners.add(el, 'change', this.onRangeChange),
    )
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.currentTarget)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onClick = this.onClick.bind(this)

    this.listeners.add(this.els.summary.element, 'click', this.onClick)
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onClick (e) {
    e.preventDefault()

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.els.content.element.animate(
      {
        opacity: [1, 0],
        transform: ['none', `translateY(0)`],
      },
      { duration: 0, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.els.content.element.animate(
      {
        opacity: [0, 1],
        transform: [`translateY(0)`, 'none'],
      },
      { duration: 0, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
